/**
 * Bonjour Cart Badge - Script léger pour afficher le compteur panier
 *
 * Usage simple (comme le tunnel):
 * 1. Inclure le script: <script src="cart-badge.js"></script>
 * 2. Ajouter l'élément: <bonjour-cart-badge data-api-key="pk_xxx"></bonjour-cart-badge>
 * 3. Utiliser les attributs sur vos éléments:
 *    - data-bonjour-cart-count : affiche le nombre d'articles
 *    - data-bonjour-cart-total : affiche le total formaté
 *
 * Le script émet l'événement 'bonjour:cart:update' avec { itemsCount, totalAmount }
 */

const STORAGE_KEY = "bjr_session";

/**
 * Détecte si on est en mode debug à partir de l'URL de l'API
 */
function isDevMode(apiUrl: string): boolean {
  try {
    const hostname = new URL(apiUrl).hostname;
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

interface CartBadgeConfig {
  apiKey: string;
  apiUrl?: string;
  locale?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
  debug?: boolean;
}

interface CartData {
  itemsCount: number;
  totalAmount: number;
}

class BonjourCartBadge {
  private config: Required<CartBadgeConfig>;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(config: CartBadgeConfig) {
    if (!config.apiUrl) {
      throw new Error("BonjourCartBadge: apiUrl is required");
    }

    this.config = {
      apiUrl: config.apiUrl,
      locale: config.locale ?? "fr-FR",
      autoRefresh: config.autoRefresh ?? false,
      refreshInterval: config.refreshInterval ?? 30000,
      debug: config.debug ?? isDevMode(config.apiUrl),
      apiKey: config.apiKey,
    };
  }

  private log(...args: unknown[]) {
    if (this.config.debug) {
      console.log("[BonjourCartBadge]", ...args);
    }
  }

  /**
   * Récupère le session token depuis localStorage
   */
  private getSessionToken(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Émet l'événement de mise à jour du panier
   */
  private emitCartUpdate(data: CartData): void {
    window.dispatchEvent(
      new CustomEvent("bonjour:cart:update", {
        detail: data,
      }),
    );
  }

  /**
   * Met à jour les éléments DOM avec le compteur
   */
  private updateDOMElements(data: CartData): void {
    // Mise à jour des éléments avec data-bonjour-cart-count
    const countElements = document.querySelectorAll("[data-bonjour-cart-count]");
    countElements.forEach((el) => {
      el.textContent = String(data.itemsCount);
      if (el instanceof HTMLElement) {
        el.style.display = data.itemsCount > 0 ? "flex" : "none";
      }
    });

    // Mise à jour des éléments avec data-bonjour-cart-total
    const totalElements = document.querySelectorAll("[data-bonjour-cart-total]");
    totalElements.forEach((el) => {
      el.textContent = new Intl.NumberFormat(this.config.locale, {
        style: "currency",
        currency: "EUR",
      }).format(data.totalAmount / 100);
    });

    this.log("DOM updated:", data);
  }

  /**
   * Récupère les données du panier depuis l'API
   */
  async fetchCart(): Promise<CartData> {
    const sessionToken = this.getSessionToken();

    if (!sessionToken) {
      this.log("No session token found");
      return { itemsCount: 0, totalAmount: 0 };
    }

    try {
      const url = `${this.config.apiUrl}/api/public/cart/by-session/${sessionToken}`;
      this.log("Fetching cart from:", url);

      const response = await fetch(url, {
        headers: {
          "X-API-Key": this.config.apiKey,
        },
      });

      if (!response.ok) {
        this.log("API error:", response.status);
        return { itemsCount: 0, totalAmount: 0 };
      }

      const data = await response.json();
      this.log("API response:", data);

      if (!data.success || !data.cart) {
        return { itemsCount: 0, totalAmount: 0 };
      }

      return {
        itemsCount: data.cart.itemsCount,
        totalAmount: data.cart.totalAmount,
      };
    } catch (error) {
      this.log("Fetch error:", error);
      return { itemsCount: 0, totalAmount: 0 };
    }
  }

  /**
   * Rafraîchit le compteur du panier
   */
  async refresh(): Promise<CartData> {
    const data = await this.fetchCart();
    this.emitCartUpdate(data);
    this.updateDOMElements(data);
    return data;
  }

  startAutoRefresh(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.refreshTimer = setInterval(() => this.refresh(), this.config.refreshInterval);
    this.log("Auto-refresh started, interval:", this.config.refreshInterval);
  }

  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      this.log("Auto-refresh stopped");
    }
  }

  /**
   * Initialise le badge
   */
  async init(): Promise<void> {
    this.log("Initializing with config:", this.config);

    // Premier chargement
    await this.refresh();

    // Auto-refresh si activé
    if (this.config.autoRefresh) {
      this.startAutoRefresh();
    }

    // Écouter les événements du widget tunnel pour sync immédiate
    window.addEventListener("bonjour:cart:update", ((event: CustomEvent<CartData>) => {
      this.updateDOMElements(event.detail);
    }) as EventListener);
  }

  destroy(): void {
    this.stopAutoRefresh();
    this.log("Destroyed");
  }
}

/**
 * Custom Element <bonjour-cart-badge>
 *
 * Usage:
 * <bonjour-cart-badge
 *   data-url="https://branche.logiciel.codelsior.fr"
 *   data-api-key="pk_xxx"
 *   data-auto-refresh="true"       (optionnel)
 *   data-refresh-interval="30000"  (optionnel, en ms)
 * ></bonjour-cart-badge>
 */
class BonjourCartBadgeElement extends HTMLElement {
  private badgeInstance: BonjourCartBadge | null = null;

  static get observedAttributes() {
    return ["data-url", "data-api-key", "data-locale"];
  }

  connectedCallback() {
    requestAnimationFrame(() => this.initBadge());
  }

  disconnectedCallback() {
    this.badgeInstance?.destroy();
    this.badgeInstance = null;
  }

  attributeChangedCallback() {
    if (this.badgeInstance) {
      this.badgeInstance.destroy();
      this.initBadge();
    }
  }

  private initBadge() {
    const apiUrl = this.dataset.url;
    const apiKey = this.dataset.apiKey;

    if (!apiUrl || !apiKey) {
      console.error("[bonjour-cart-badge] data-url et data-api-key sont requis");
      return;
    }

    const config: CartBadgeConfig = {
      apiKey,
      apiUrl,
      locale: this.dataset.locale,
      autoRefresh: this.dataset.autoRefresh === "true",
      refreshInterval: this.dataset.refreshInterval
        ? parseInt(this.dataset.refreshInterval, 10)
        : undefined,
    };

    this.badgeInstance = new BonjourCartBadge(config);

    // Exposer globalement pour permettre refresh manuel
    (window as any).BonjourCartBadge = this.badgeInstance;

    this.badgeInstance.init();
  }
}

// Enregistrer le Custom Element
if (typeof window !== "undefined" && !customElements.get("bonjour-cart-badge")) {
  customElements.define("bonjour-cart-badge", BonjourCartBadgeElement);
}

// Export pour utilisation en module
export { BonjourCartBadge, type CartBadgeConfig, type CartData };
